import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";

// --------------------------------------------------
// API
// --------------------------------------------------
const API =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "";

// --------------------------------------------------
// TODAY
// --------------------------------------------------
const getToday = () => {
  const d = new Date();

  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${d.getFullYear()}-${month}-${day}`;
};

// --------------------------------------------------
// DATE FORMAT
// --------------------------------------------------
const formatDate = (value) => {
  if (!value) return "-";

  if (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(value)
  ) {
    const [year, month, day] = value.split("-");

    return `${day}/${month}/${year}`;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-IN");
};

// --------------------------------------------------
// GET SESSION ID
// --------------------------------------------------
const getSessionId = () => {
  const keys = [
    "currentSessionId",
    "sessionId",
    "selectedSessionId",
    "activeSessionId",
  ];

  for (const key of keys) {
    const value = localStorage.getItem(key);

    if (!value) continue;

    try {
      const parsed = JSON.parse(value);

      if (
        typeof parsed === "string" &&
        parsed.trim()
      ) {
        return parsed.trim();
      }

      if (parsed?._id) {
        return parsed._id;
      }

      if (parsed?.id) {
        return parsed.id;
      }

      if (parsed?.sessionId) {
        return parsed.sessionId;
      }
    } catch {
      if (value.trim()) {
        return value.trim();
      }
    }
  }

  return "";
};

// --------------------------------------------------
// STATUS NORMALIZER
//
// UI:
// Sent
// Not Sent
//
// Backend:
// sent              -> Sent
// partial           -> Sent
// report submitted  -> Sent
// attempted         -> Sent
// anything else     -> Not Sent
// --------------------------------------------------
const normalizeStatus = (status) => {
  if (!status) {
    return "Not Sent";
  }

  const value = String(status)
    .trim()
    .toLowerCase();

  if (
    value === "sent" ||
    value === "partial" ||
    value === "report submitted" ||
    value === "attempted"
  ) {
    return "Sent";
  }

  return "Not Sent";
};

// --------------------------------------------------
// COMPONENT
// --------------------------------------------------
const InchargeReports = () => {
  // --------------------------------------------------
  // DATE FILTER
  // --------------------------------------------------
  const [selectedDate, setSelectedDate] =
    useState(getToday());

  // --------------------------------------------------
  // STATUS FILTER
  // --------------------------------------------------
  const [selectedStatus, setSelectedStatus] =
    useState("All");

  // --------------------------------------------------
  // HOMEWORK / NOTEBOOK FILTER
  // --------------------------------------------------
  const [selectedType, setSelectedType] =
    useState("All");

  // --------------------------------------------------
  // REPORTS
  // --------------------------------------------------
  const [reports, setReports] = useState([]);

  // --------------------------------------------------
  // SUMMARY
  // --------------------------------------------------
  const [summary, setSummary] = useState({
    totalClassIncharges: 0,
    sentClassIncharges: 0,
    notSentClassIncharges: 0,
  });

  // --------------------------------------------------
  // LOADING
  // --------------------------------------------------
  const [loading, setLoading] = useState(false);

  // --------------------------------------------------
  // ERROR
  // --------------------------------------------------
  const [error, setError] = useState("");

  // --------------------------------------------------
  // FETCH REPORTS
  // --------------------------------------------------
  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const sessionId = getSessionId();

      if (!sessionId) {
        setReports([]);

        setSummary({
          totalClassIncharges: 0,
          sentClassIncharges: 0,
          notSentClassIncharges: 0,
        });

        setError(
          "Session ID not found. Please select an active session."
        );

        return;
      }

      const response = await axios.get(
        `${API}/api/adminReports/incharge-reports`,
        {
          params: {
            sessionId,
            date: selectedDate,
          },
        }
      );

      const data = response?.data || {};

      if (data.success === false) {
        throw new Error(
          data.message ||
            "Failed to load Class Incharge data."
        );
      }

      const backendReports = Array.isArray(
        data.reports
      )
        ? data.reports
        : [];

      // --------------------------------------------------
      // NORMALIZE REPORTS
      // --------------------------------------------------
      const normalizedReports =
        backendReports.map((report, index) => {
          const status = normalizeStatus(
            report.status
          );

          return {
            ...report,

            _uiId:
              report.classInchargeId ||
              report.teacherId ||
              `${report.className || "class"}-${
                report.sectionName || "section"
              }-${index}`,

            uiStatus: status,

            homeworkCount:
              typeof report.homeworkCount ===
              "number"
                ? report.homeworkCount
                : 0,

            notebookCount:
              typeof report.notebookCount ===
              "number"
                ? report.notebookCount
                : 0,

                
            
        parentsReportSent:
  typeof report.parentsReportSent === "number"
    ? report.parentsReportSent
    : 0,

            displayDate:
              report.lastSentAt ||
              report.date ||
              selectedDate,
          };
        });

      setReports(normalizedReports);

      // --------------------------------------------------
      // SUMMARY
      // --------------------------------------------------
      let sentCount = 0;
      let notSentCount = 0;

      normalizedReports.forEach((report) => {
        if (
          report.uiStatus === "Sent"
        ) {
          sentCount += 1;
        } else {
          notSentCount += 1;
        }
      });

      setSummary({
        totalClassIncharges:
          typeof data.totalClassIncharges ===
          "number"
            ? data.totalClassIncharges
            : normalizedReports.length,

        sentClassIncharges:
          sentCount,

        notSentClassIncharges:
          notSentCount,
      });
    } catch (error) {
      console.error(
        "Class Incharge Reports Error:",
        error
      );

      setReports([]);

      setSummary({
        totalClassIncharges: 0,
        sentClassIncharges: 0,
        notSentClassIncharges: 0,
      });

      setError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to load Class Incharge data."
      );
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  // --------------------------------------------------
  // LOAD DATA
  // --------------------------------------------------
  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // --------------------------------------------------
  // RESET FILTERS
  // --------------------------------------------------
  const resetFilters = () => {
    setSelectedDate(getToday());
    setSelectedStatus("All");
    setSelectedType("All");
  };

  // --------------------------------------------------
  // FILTER REPORTS
  // --------------------------------------------------
  const filteredReports = reports.filter(
    (report) => {
      // ----------------------------------------------
      // STATUS FILTER
      // ----------------------------------------------
      const statusMatch =
        selectedStatus === "All" ||
        normalizeStatus(
          report.uiStatus ||
            report.status
        ) === selectedStatus;

      // ----------------------------------------------
      // TYPE FILTER
      // ----------------------------------------------
      let typeMatch = true;

      if (selectedType === "Homework") {
        typeMatch =
          Number(report.homeworkCount || 0) > 0;
      }

      if (selectedType === "Notebook") {
        typeMatch =
          Number(report.notebookCount || 0) > 0;
      }

      return statusMatch && typeMatch;
    }
  );

  // --------------------------------------------------
  // STATUS UI
  // --------------------------------------------------
  const renderStatus = (status) => {
    const normalizedStatus =
      normalizeStatus(status);

    if (
      normalizedStatus === "Sent"
    ) {
      return (
        <span className="inline-flex rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
          Sent
        </span>
      );
    }

    return (
      <span className="inline-flex rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">
        Not Sent
      </span>
    );
  };

  // --------------------------------------------------
  // UI
  // --------------------------------------------------
  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl">

        {/* ==================================================
            HEADER
        ================================================== */}
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">

          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              Class Incharge Data
            </h1>

            <p className="mt-1 text-sm text-gray-500">
              Class incharge-wise WhatsApp homework
              and notebook report status.
            </p>
          </div>

          <button
            type="button"
            onClick={fetchReports}
            disabled={loading}
            className="rounded-lg bg-green-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading
              ? "Refreshing..."
              : "Refresh"}
          </button>

        </div>

        {/* ==================================================
            SUMMARY CARDS
            FIRST
        ================================================== */}
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">

          {/* TOTAL CLASS INCHARGES */}
          <div className="rounded-xl bg-white p-5 shadow-sm">

            <div className="flex items-center justify-between">

              <div>
                <p className="text-sm text-gray-500">
                  Total Class Incharges
                </p>

                <h2 className="mt-1 text-3xl font-bold text-gray-800">
                  {
                    summary.totalClassIncharges
                  }
                </h2>
              </div>

              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-xl text-blue-600">
                <i className="fas fa-users"></i>
              </div>

            </div>

          </div>

          {/* SENT */}
          <div className="rounded-xl bg-white p-5 shadow-sm">

            <div className="flex items-center justify-between">

              <div>
                <p className="text-sm text-gray-500">
                  Sent
                </p>

                <h2 className="mt-1 text-3xl font-bold text-green-600">
                  {
                    summary.sentClassIncharges
                  }
                </h2>
              </div>

              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-xl text-green-600">
                <i className="fas fa-check-circle"></i>
              </div>

            </div>

          </div>

          {/* NOT SENT */}
          <div className="rounded-xl bg-white p-5 shadow-sm">

            <div className="flex items-center justify-between">

              <div>
                <p className="text-sm text-gray-500">
                  Not Sent
                </p>

                <h2 className="mt-1 text-3xl font-bold text-red-600">
                  {
                    summary.notSentClassIncharges
                  }
                </h2>
              </div>

              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-xl text-red-600">
                <i className="fas fa-times-circle"></i>
              </div>

            </div>

          </div>

        </div>

        {/* ==================================================
            FILTERS
            JUST ABOVE TABLE
        ================================================== */}
        <div className="mb-4 rounded-xl bg-white p-4 shadow-sm">

          <div className="flex flex-col gap-4 lg:flex-row lg:items-end">

            {/* DATE */}
            <div className="w-full lg:w-auto">

              <label className="mb-1 block text-sm font-semibold text-gray-700">
                Report Date
              </label>

              <input
                type="date"
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(
                    e.target.value
                  );
                }}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 lg:w-auto"
              />

            </div>

            {/* TYPE */}
            <div className="w-full lg:w-auto">

              <label className="mb-1 block text-sm font-semibold text-gray-700">
                Type
              </label>

              <select
                value={selectedType}
                onChange={(e) => {
                  setSelectedType(
                    e.target.value
                  );
                }}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 lg:w-44"
              >
                <option value="All">
                  All
                </option>

                <option value="Homework">
                  Homework
                </option>

                <option value="Notebook">
                  Notebook
                </option>
              </select>

            </div>

            {/* STATUS */}
            <div className="w-full lg:w-auto">

              <label className="mb-1 block text-sm font-semibold text-gray-700">
                Status
              </label>

              <select
                value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(
                    e.target.value
                  );
                }}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 lg:w-44"
              >
                <option value="All">
                  All
                </option>

                <option value="Sent">
                  Sent
                </option>

                <option value="Not Sent">
                  Not Sent
                </option>
              </select>

            </div>

            {/* TODAY */}
            <button
              type="button"
              onClick={() => {
                setSelectedDate(
                  getToday()
                );
              }}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
            >
              Today
            </button>

            {/* RESET */}
            <button
              type="button"
              onClick={resetFilters}
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-100"
            >
              Reset
            </button>

          </div>

        </div>

        {/* ==================================================
            ERROR
        ================================================== */}
        {error && (
          <div className="mb-4 flex items-center justify-between gap-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">

            <span>{error}</span>

            <button
              type="button"
              onClick={fetchReports}
              className="whitespace-nowrap rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700"
            >
              Retry
            </button>

          </div>
        )}

        {/* ==================================================
            LOADING
        ================================================== */}
        {loading && (
          <div className="mb-4 rounded-xl bg-white px-5 py-4 text-center text-sm text-gray-500 shadow-sm">
            Loading Class Incharge reports...
          </div>
        )}

        {/* ==================================================
            TABLE
        ================================================== */}
        <div className="overflow-hidden rounded-xl bg-white shadow-sm">

          {/* TABLE TITLE */}
          <div className="border-b border-gray-200 px-5 py-4">

            <h2 className="text-lg font-bold text-gray-800">
              Class Incharge Data
            </h2>

            <p className="mt-1 text-xs text-gray-500">
              One row per class incharge
            </p>

          </div>

          {/* TABLE */}
          <div className="overflow-x-auto">

            <table className="min-w-full text-left text-sm">

              {/* TABLE HEADER */}
              <thead className="bg-gray-100 text-gray-700">

                <tr>

                  <th className="px-4 py-3">
                    #
                  </th>

                  <th className="px-4 py-3">
                    Class Incharge
                  </th>

                  <th className="px-4 py-3">
                    Class
                  </th>

                  <th className="px-4 py-3">
                    Section
                  </th>

                  <th className="px-4 py-3">
                    Student
                  </th>

                  <th className="px-4 py-3">
                    Date
                  </th>

                  <th className="px-4 py-3">
                    Status
                  </th>

                </tr>

              </thead>

              {/* TABLE BODY */}
              <tbody>

                {filteredReports.length === 0 &&
                !loading ? (
                  <tr>

                    <td
                      colSpan="6"
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      {reports.length === 0
                        ? "No class incharge reports found for this date."
                        : "No reports found for the selected filters."}
                    </td>

                  </tr>
                ) : (
                  filteredReports.map(
                    (report, index) => (
                      <tr
                        key={report._uiId}
                        className="border-t hover:bg-gray-50"
                      >

                        {/* # */}
                        <td className="px-4 py-3">
                          {index + 1}
                        </td>

                        {/* CLASS INCHARGE */}
                        <td className="px-4 py-3 font-semibold text-gray-800">
                          {report.classInchargeName ||
                            report.teacherName ||
                            "-"}
                        </td>

                        {/* CLASS */}
                        <td className="px-4 py-3">
                          {report.className ||
                            "-"}
                        </td>

                        {/* SECTION */}
                        <td className="px-4 py-3">
                          {report.sectionName ||
                            "-"}
                        </td>

{/* STUDENTS */}
<td className="px-4 py-3">
  <span className="inline-flex min-w-[42px] justify-center rounded-full bg-green-100 px-3 py-1 text-xs font-bold text-green-700">
    {report.parentsReportSent}
  </span>
</td>

                        {/* DATE */}
                        <td className="whitespace-nowrap px-4 py-3">
                          {formatDate(
                            report.displayDate
                          )}
                        </td>

                        {/* STATUS */}
                        <td className="px-4 py-3">
                          {renderStatus(
                            report.uiStatus
                          )}
                        </td>

                      </tr>
                    )
                  )
                )}

              </tbody>

            </table>

          </div>
        </div>

      </div>
    </div>
  );
};

export default InchargeReports;